import { useRef, useState } from "react";

import { useFormikContext } from "formik";
import { Upload } from "neetoicons";
import { Button, Toastr } from "neetoui";
import { MultiEmailInput } from "neetoui/formik";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import BulkUploadPane from "./BulkUploadPane";
import { CSV_FILE_SIZE_IN_KB, SIZES } from "./constants";
import { parseCsvEmails } from "./utils";

const SendToFields = ({
  emailInputProps,
  ccInputProps,
  bccInputProps,
  showUploadCsvButton = false,
  showCcBccButton = false,
  showCcField = false,
  showBccField = false,
  showSendToField = true,
}) => {
  const [isBulkUploadPaneOpen, setIsBulkUploadPaneOpen] = useState(false);
  const sendToFieldRef = useRef();
  const {
    values: {
      showCopyEmails,
      [emailInputProps.name || "sendTo"]: existingSendToEmails,
    },
    setFieldValue,
  } = useFormikContext();
  const { t } = useTranslation();

  const handleCSVFile = file => {
    try {
      const onSuccess = values =>
        setFieldValue(emailInputProps.name || "sendTo", [
          ...existingSendToEmails,
          ...values,
        ]);

      parseCsvEmails(file, onSuccess);
      setIsBulkUploadPaneOpen(false);
      sendToFieldRef.current?.focus();
    } catch (error) {
      Toastr.error(error);
    }
  };

  const handelCcBccButtonClick = e => {
    e.stopPropagation();
    setFieldValue("showCopyEmails", !showCopyEmails);
  };

  return (
    <div
      className="relative w-full flex-col gap-2"
      data-testid="send-to-text-field"
    >
      {showUploadCsvButton && (
        <Button
          className="absolute end-1 top-0"
          data-testid="bulk-upload-button"
          icon={Upload}
          label={t("neetoMolecules.shareViaLink.bulkUpload")}
          size="small"
          style="link"
          onClick={() => setIsBulkUploadPaneOpen(true)}
        />
      )}
      {showSendToField && (
        <div className="neeto-ui-border-gray-200 w-full border-b pb-1">
          <MultiEmailInput
            name="sendTo"
            ref={sendToFieldRef}
            {...emailInputProps}
            suffix={
              showCcBccButton && (
                <Button
                  data-testid="cc-bcc-button"
                  label={t("neetoMolecules.emailForm.labels.ccBcc")}
                  size="small"
                  style="link"
                  onClick={handelCcBccButtonClick}
                  onKeyDown={event => event.stopPropagation()}
                />
              )
            }
          />
        </div>
      )}
      {(showCopyEmails || showCcField || showBccField) && (
        <div className="mt-5 flex w-full flex-col gap-5">
          {(showCopyEmails || showCcField) && (
            <div className="neeto-ui-border-gray-200 w-full border-b pb-1">
              <MultiEmailInput name="sendToBcc" {...ccInputProps} />
            </div>
          )}
          {(showCopyEmails || showBccField) && (
            <div className="neeto-ui-border-gray-200 w-full border-b pb-1">
              <MultiEmailInput name="sendToCc" {...bccInputProps} />
            </div>
          )}
        </div>
      )}
      <BulkUploadPane
        fileSize={CSV_FILE_SIZE_IN_KB}
        handleFileSubmit={handleCSVFile}
        isOpen={isBulkUploadPaneOpen}
        onClose={() => setIsBulkUploadPaneOpen(false)}
      />
    </div>
  );
};

const inputPropTypes = PropTypes.shape({
  name: PropTypes.string,
  display: PropTypes.node,
  label: PropTypes.string,
  editButtonTooltip: PropTypes.string,
  editButtonDataTestid: PropTypes.string,
  placeholder: PropTypes.string,
  dataTestid: PropTypes.string,
  onChange: PropTypes.func,
  suffix: PropTypes.node,
  prefix: PropTypes.node,
  className: PropTypes.string,
  nakedInput: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(SIZES)),
  type: PropTypes.string,
  maxLength: PropTypes.number,
  contentSize: PropTypes.number,
  unlimitedChars: PropTypes.bool,
  required: PropTypes.bool,
});

SendToFields.propTypes = {
  /**
   * Input props for sendTo input field.
   */
  emailInputProps: inputPropTypes,
  /**
   * Input props for CC input field.
   */
  ccInputProps: inputPropTypes,
  /**
   * Input props for BCC input field.
   */
  bccInputProps: inputPropTypes,
  /**
   * Flag to determine whether the 'FilePicker' button is displayed.
   */
  showUploadCsvButton: PropTypes.bool,
  /**
   * Flag to determine whether the 'Cc/Bcc' button is displayed.
   */
  showCcBccButton: PropTypes.bool,
  /**
   * To show or hide the CC field.
   */
  showCcField: PropTypes.bool,
  /**
   * To show or hide the BCC field.
   */
  showBccField: PropTypes.bool,
  /**
   * To show or hide the "Send to" field.
   */
  showSendToField: PropTypes.bool,
};

export default SendToFields;
